/**
 * Network graph layer types and configuration
 */

export type LayerType = 'topology' | 'physical' | 'vlan' | 'stp' | 'throughput' | 'ospf';

export interface LayerConfig {
  id: LayerType;
  label: string;
  description: string;
  enabled: boolean;
}

export const LAYER_CONFIGS: Record<LayerType, LayerConfig> = {
  topology: {
    id: 'topology',
    label: 'Topology',
    description: 'Basic network topology showing devices and physical connections',
    enabled: true,
  },
  physical: {
    id: 'physical',
    label: 'Physical',
    description: 'Physical link speeds (10Mbps, 100Mbps, 1Gbps, 10Gbps)',
    enabled: true,
  },
  vlan: {
    id: 'vlan',
    label: 'VLAN',
    description: 'VLAN membership and segmentation overlay',
    enabled: true,
  },
  stp: {
    id: 'stp',
    label: 'STP',
    description: 'Spanning-tree state per instance — forwarding vs. blocked, root bridges, and asymmetric conflicts',
    enabled: true,
  },
  throughput: {
    id: 'throughput',
    // Renamed because the lens now carries loss as well as rate: a link is
    // drawn as needing attention when it is dropping frames, not only when it
    // is busy. The id stays `throughput` so existing URLs keep working.
    label: 'Traffic',
    description: 'How much each link is carrying, and which links are losing frames',
    enabled: true,
  },
  ospf: {
    id: 'ospf',
    label: 'OSPF',
    description: 'OSPF routing areas and neighbor relationships',
    enabled: true,
  },
};

/**
 * Overlays are orthogonal to layers: layers are mutually-exclusive base
 * lenses (the tab group), overlays are independent stackable toggles drawn
 * on top of whichever layer is active.
 */
export type OverlayType = 'findings' | 'endpoints' | 'path';

export interface OverlayConfig {
  id: OverlayType;
  label: string;
  description: string;
  /** Whether the overlay is on when the URL has no ?overlays= param */
  defaultOn: boolean;
  /**
   * Hidden overlays get no toggle chip but still activate via the URL —
   * used to keep in-development overlays (S0 path) out of prod chrome
   * while deep links from their own feature pages keep working.
   */
  hidden?: boolean;
}

export const OVERLAY_CONFIGS: Record<OverlayType, OverlayConfig> = {
  findings: {
    id: 'findings',
    label: 'Issues',
    description: 'Audit finding badges on affected devices',
    defaultOn: true,
  },
  endpoints: {
    id: 'endpoints',
    label: 'Endpoints',
    description: 'End-user devices attached to switches and APs',
    defaultOn: false,
  },
  path: {
    id: 'path',
    label: 'Path',
    description: 'Highlight a traced path from Path Check (S0 skeleton)',
    defaultOn: false,
    hidden: !import.meta.env.DEV,
  },
};

/**
 * Data types for layer-specific information
 */

export interface VlanData {
  vlanId: number;
  name: string;
  color: string;
}

export interface ThroughputData {
  /** Historical throughput values (bps) for sparkline */
  history: number[];
  /** Current throughput in bps */
  current: number;
  /**
   * Link speed in bps, or `null` when the port's speed is unknown.
   *
   * The backend used to substitute a gigabit here for any port it had not
   * discovered a speed for. In production 52.6% of interfaces carry
   * `speed_mbps = 0` and a further 3.8% are NULL, so most of the fleet was
   * being measured against a made-up denominator.
   */
  capacity: number | null;
  /**
   * Utilization percentage (0-100), or `null` when `capacity` is unknown.
   *
   * Never conflate `null` with `0`: an unknown-speed port used to arrive as a
   * confident 0% and got painted the same green as a genuinely idle link.
   */
  utilization: number | null;
}

export interface ErrorData {
  /** Error rate (errors per second) */
  errorsPerSec: number;
  /** Discard rate (discards per second) */
  discardsPerSec: number;
  /** Whether there are active errors or discards */
  hasErrors: boolean;
}

export interface OspfData {
  areaId: string;
  routerId: string;
  neighborState: 'down' | 'init' | 'two-way' | 'exstart' | 'exchange' | 'loading' | 'full';
  cost: number;
}

export type LinkSpeed = '10m' | '100m' | '1g' | '10g' | 'unknown';

export interface PhysicalData {
  /** Link speed category */
  speed: LinkSpeed;
  /** Raw speed in bps for display */
  speedBps?: number;
}

/**
 * Extended node data with layer-specific attributes
 */
export interface LayerNodeData {
  vlans?: VlanData[];
  throughput?: ThroughputData;
  ospf?: OspfData;
}

/**
 * Extended edge data with layer-specific attributes
 */
export interface LayerEdgeData {
  vlans?: number[];  // VLAN IDs carried on this link
  throughput?: {
    inbound: ThroughputData;
    outbound: ThroughputData;
    errors: ErrorData;
  };
  ospfCost?: number;
  physical?: PhysicalData;
}
